import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { practiceAreas } from '@/mocks/homeData';

export default function PracticeSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [selectedArea, setSelectedArea] = useState<typeof practiceAreas[0] | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const openModal = (area: typeof practiceAreas[0]) => setSelectedArea(area);
  const closeModal = () => setSelectedArea(null);

  const goToContact = () => {
    setSelectedArea(null);
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="practice"
      ref={sectionRef}
      className="relative w-full py-16 md:py-24 bg-background-50"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">
            Practice Areas
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('practice.title')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-500">
            {t('practice.subtitle')}
          </p>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {practiceAreas.map((area, index) => (
            <div
              key={index}
              onClick={() => openModal(area)}
              className="group bg-background-100 rounded-xl p-5 md:p-6 border-2 border-background-200/70 hover:border-accent-400 transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent-100 rounded-lg group-hover:bg-accent-200 transition-colors flex-shrink-0">
                  <i className={`${area.icon} text-accent-600 text-lg md:text-xl`}></i>
                </span>
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
                  {area.title}
                </h3>
                <span className="ml-auto w-6 h-6 flex items-center justify-center text-accent-600 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-s-line text-base"></i>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedArea && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          />
          {/* Modal content */}
          <div className="relative bg-background-50 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 z-10">
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Modal header */}
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-lg">
                <i className={`${selectedArea.icon} text-accent-600 text-xl`}></i>
              </span>
              <h3 className="font-heading text-xl font-bold text-foreground-900">
                {selectedArea.title}
              </h3>
            </div>

            {/* Keyword tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedArea.keywords.map((keyword, i) => (
                <span
                  key={i}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-secondary-100 text-secondary-900"
                >
                  {keyword}
                </span>
              ))}
            </div>

            {/* Modal body */}
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-8">
              {selectedArea.desc}
            </p>

            {/* Process steps */}
            <h4 className="flex items-center gap-2 font-heading text-sm md:text-base font-bold text-foreground-950 mb-4">
              <i className="ri-route-line text-accent-600"></i>
              진행 절차
            </h4>
            <ol className="space-y-3 mb-8">
              {selectedArea.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                    {i + 1}
                  </span>
                  <span className="text-foreground-700 text-sm leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>

            {/* CTA */}
            <button
              onClick={goToContact}
              className="w-full flex items-center justify-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium py-3 rounded-md transition-colors cursor-pointer"
            >
              <i className="ri-calendar-check-line"></i>
              상담 예약하기
            </button>
          </div>
        </div>
      )}
    </section>
  );
}